import {
	Box,
	Paper,
	Stack,
	TablePagination,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { AiSearch, DashboardTable } from "components";
import { FilterEnum } from "enums/filterEnum";
import { ContainmentFilterKey } from "enums/filters";
import * as containmentFilter from "enums/filters/containment";
import * as dotEnv from "lib/dotEnv";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { MuiTablePagination } from "lib/styles/pagination";
import * as util from "lib/util";
import { append } from "lib/mods/util";
import { Containment } from "models";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContainmentsSlice } from "store";
import { DashboardFiltersComponent } from "./DashboardFilters";
import { DashboardFilters, DashboardFiltersCtx } from "./types";
import { Tabs, Tab } from "@mui/material";

/* eslint-disable @typescript-eslint/no-non-null-assertion */
export const FiltersCtx = React.createContext<DashboardFiltersCtx>(null!);

/** Delay (ms) before sending search API request when only the filter bar main "Search..." field (`q` / `query`) changes. */
const searchDebounceMs = 1500;

export function DashboardComponent() {
	const theme = useTheme();
	type sectorTab = { key: string; label: string; count: number };
	const isAiSearchEnabled = dotEnv.Get("AI_SEARCH_ENABLED").Equal("1");

	// Create filter enums once
	const filterEnums = useMemo(
		() =>
			append<FilterEnum<ContainmentFilterKey, any>>(
				containmentFilter.ContainmentSearchFields,
				containmentFilter.Text({
					field: "impactedSectorTypes.sectorType.sector.id" as ContainmentFilterKey,
					label: "Impacted Sector Types Sector Id",
				}),
				//impactedPlantsFilter.Dropdown()
			),
		[]
	);

	// Use the optimized hook
	const ctx = hooks.UseSearchFilters<Containment, DashboardFilters>(
		filterEnums
	);
	
	const snack = hooks.UseSnackBar();
	const mccApi = hooks.UseMccApi();
	const permissions = hooks.UseHasPermission();	
	const { sectors } = hooks.UseSectorsAndPermissions();
	const sectorsReady = (sectors || []).length > 0;

	// Convert user-accessible sector names -> sector IDs for filtering by ID
	const allAccessibleSectorIds = useMemo(() => {
		const nameSet = new Set((permissions.allAccessibleSectorNames || []).map(String));
		return (sectors || [])
			.filter((s) => nameSet.has(String(s.name)))
			.map((s) => String(s.id))
			.filter(Boolean);
	}, [permissions.allAccessibleSectorNames, sectors]);

	const allAccessibleSectorIdsCsv = useMemo(
		() => allAccessibleSectorIds.join(","),
		[allAccessibleSectorIds]
	);
	const [filtersOpen, setFiltersOpen] = useState(false); 

	const [loading, setLoading] = useState(false);
	const [containments, setContainments] = useState<Containment[]>([]);	
	const [totalRecords, setTotalRecords] = useState(0);
	const [noData, setNoData] = useState(false);
	const [tabs, setTabs] = useState<sectorTab[]>([]);
	const [activeSectorTab, setActiveSectorTab] = useState<string>("All");	
	const hasFilters = !ctx.filters.filters.Keys().IsEmpty();
	const shouldShowFilters = filtersOpen;

	/** Payload JSON omitting `filterGroups` — only the main "Search..." text (`q` / `query`) goes there. */
	const searchPayloadExcludingMainSearchBarKey = useMemo(() => {
		const p = ctx.searchPayload;
		const { filterGroups: _, ...restSearch } = p.searchFilters;
		return JSON.stringify({
			sortList: p.sortList,
			paging: p.paging,
			resultList: p.resultList,
			searchFilters: { ...restSearch, filterGroups: [] },
		});
	}, [ctx.searchPayload]);

	const searchPayloadRef = useRef(ctx.searchPayload);
	searchPayloadRef.current = ctx.searchPayload;
	const payloadExcludingMainSearchRef = useRef<string | null>(null);
	const searchRequestSeqRef = useRef(0);

	// Memoize handlers with proper dependencies
	const containmentActions = hooks.UseActions(ContainmentsSlice.actions);

	useEffect(() => {
		if (!sectorsReady) {
			return;
		}
		ctx.setFilter({
			"impactedSectorTypes.sectorType.sector.id":
				activeSectorTab === "All"
					? allAccessibleSectorIdsCsv
					: activeSectorTab,
		} as any);

		ctx.setPageParams({ page: 1, pageSize: ctx.filters.paging.pageSize });
	}, [activeSectorTab, allAccessibleSectorIdsCsv, sectorsReady]);

	useEffect(() => {
		if (!sectorsReady) {
			return;
		}
		const prev = payloadExcludingMainSearchRef.current;
		const debounceMainSearchOnly =
			prev !== null && prev === searchPayloadExcludingMainSearchBarKey;
		payloadExcludingMainSearchRef.current =
			searchPayloadExcludingMainSearchBarKey;
		const delay = debounceMainSearchOnly ? searchDebounceMs : 500;

		const timeout = setTimeout(() => {
			const fetchContainments = async () => {
				const seq = ++searchRequestSeqRef.current;
				setLoading(true);
				try {
					const result = await mccApi.SearchContainments(
						searchPayloadRef.current
					);

					if (seq !== searchRequestSeqRef.current) {
						return;
					}

					containmentActions.Set(result.resultList);
					setContainments(result.resultList);				
					setTotalRecords(result.paging.totalRecords);
					const sectorCounts = (result.sectorCounts ?? {}) as Record<string, number>;
					// Containments can impact multiple sectors, so summing per-sector counts double-counts
					// any cross-sector containment. The backend now returns an explicit distinct
					// "allCount" for this exact reason; fall back to the inflated sum only if an older
					// backend (without allCount) is responding, so the UI stays usable during rollout.
					const allTabCount = typeof result.allCount === "number"
						? result.allCount
						: Object.values(sectorCounts).reduce((sum, value) => sum + value, 0);

					const idToName = new Map<string, string>(
						(sectors || []).map((s) => [String(s.id), String(s.name)] as const)
					);
					const nameToId = new Map<string, string>(
						(sectors || []).map((s) => [String(s.name), String(s.id)] as const)
					);

					// Normalize keys to sector IDs (backend may return IDs or names)
					const byId = new Map<string, number>();
					for (const [k, v] of Object.entries(sectorCounts)) {
						const keyStr = String(k);
						const sectorId = nameToId.get(keyStr) || keyStr;
						byId.set(sectorId, (byId.get(sectorId) || 0) + (Number(v) || 0));
					}

					const normalizedTabs: sectorTab[] = [...byId.entries()].map(([sectorId, count]) => ({
						key: sectorId,
						label: idToName.get(sectorId) || sectorId,
						count,
					}));

					setTabs([{ key: "All", label: "All", count: allTabCount }, ...normalizedTabs]);
					setNoData(
						!!result.errorList
							?.First()
							.Map((o) =>
								o.message.ToLower().Contains("no data found")
							)
							.UnwrapOr(false)
					);

					if (result.paging.page !== ctx.filters.paging.page) {
						ctx.setPageParams({
							page: result.paging.page,
							pageSize: ctx.filters.paging.pageSize,
						});
					}
				} catch (err) {
					if (seq !== searchRequestSeqRef.current) {
						return;
					}
					const msg = util.GetMsgFromError(String(err));
					snack.open(result.Err(msg));
					setContainments([]);
					setTotalRecords(0);
				} finally {
					if (seq === searchRequestSeqRef.current) {
						setLoading(false);
					}
				}
			};

			fetchContainments();
		}, delay);

		return () => clearTimeout(timeout);
	}, [ctx.searchPayload, mccApi, searchPayloadExcludingMainSearchBarKey, sectorsReady, sectors]);

	const handleChangePage = useCallback(
		(_: unknown, newPage: number) => {
			const totalPages = Math.ceil(
				totalRecords / ctx.filters.paging.pageSize
			);
			const boundedPage = Math.max(0, Math.min(newPage, totalPages - 1));

			ctx.setPageParams({
				page: boundedPage + 1,
				pageSize: ctx.filters.paging.pageSize,
			});
		},
		[ctx, totalRecords]
	);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const val = parseInt(event.target.value, 10);
			if (isNaN(val)) {
				log.Fatal("Invalid page size");
			}

			ctx.setPageParams({
				page: 1,
				pageSize: val,
			});
		},
		[ctx.setPageParams]
	);

	const toggleFilters = useCallback(() => {
		setFiltersOpen((prev) => !prev);
	}, []);

	const tableProps = useMemo(() => ({ noData }), [noData]);
	const showPagination = containments.length > 0;

	// CRITICAL: ctx is already memoized from UseSearchFilters, so we can use it directly
	// This prevents the context from changing on every render
	return (
		<FiltersCtx.Provider value={ctx}>
			<Box
				sx={{
					p: 2,
					background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.5)}, ${alpha(theme.palette.background.default, 0.5)})`,
					borderRadius: 4,
				}}
			>
				<Stack spacing={3}>
					{/* Page Title */}
					<Typography variant="h1">Dashboard</Typography>

					{/* Search Chat Component */}
					{/* Conditionally show AI search based on environment variable */}
					{isAiSearchEnabled && <AiSearch />}

					{/* Filters */}
					{shouldShowFilters && <DashboardFiltersComponent />}
					{sectorsReady && tabs.length > 0 && (
						<Tabs
							value={activeSectorTab}
							onChange={(_, v) => setActiveSectorTab(v)}
						>
							{tabs.map((t) => (
								<Tab
									key={t.key}
									value={t.key}
									label={`${t.label} (${t.count})`}
								/>
							))}
						</Tabs>
					)}
					{/* Dashboard Table */}
					<Box sx={{ position: "relative" }}>
						<DashboardTable
							tableProps={tableProps}
							containments={containments}
							page={ctx.filters.paging.page}
							rowsPerPage={ctx.filters.paging.pageSize}
							loading={loading}
							title="Containments"
							onToggleFilters={toggleFilters}
							filtersActive={hasFilters}
							filtersOpen={filtersOpen}
						/>

						{showPagination && (
							<Paper
								elevation={0}
								sx={{
									mt: 2,
									p: 1,
									borderRadius: 2,
									backgroundColor: alpha(
										theme.palette.background.paper,
										0.7
									),
									backdropFilter: "blur(10px)",
								}}
							>
								<div data-test-selector="dashboard-table-pagination">
									<TablePagination
										rowsPerPageOptions={[5, 10, 25]}
										component="div"
										count={totalRecords}
										rowsPerPage={
											ctx.filters.paging.pageSize
										}
										page={Math.max(
											0,
											ctx.filters.paging.page - 1
										)}
										onPageChange={handleChangePage}
										onRowsPerPageChange={
											handleChangeRowsPerPage
										}
										labelDisplayedRows={({
											from,
											to,
											count,
										}) => {
											const adjustedFrom = Math.max(
												1,
												from
											);
											const adjustedTo = Math.min(
												to,
												count
											);
											return `${adjustedFrom}–${adjustedTo} of ${count}`;
										}}
										sx={{
											...MuiTablePagination(),
										}}
									/>
								</div>
							</Paper>
						)}
					</Box>
				</Stack>
			</Box>
		</FiltersCtx.Provider>
	);
}